import { useState, useEffect } from 'react';

const VISIT_COUNT_KEY = 'ir_visit_count';
const BANNER_DISMISSED_KEY = 'ir_install_banner_dismissed';
const INSTALLED_KEY = 'ir_app_installed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function safeLocalStorage(action: 'get' | 'set', key: string, value?: string): string | null {
  try {
    if (action === 'get') return localStorage.getItem(key);
    if (action === 'set' && value !== undefined) localStorage.setItem(key, value);
  } catch {
    // localStorage unavailable
  }
  return null;
}

function isStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

export default function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    try {
      if (isStandalone()) return;
      if (safeLocalStorage('get', INSTALLED_KEY) === 'true') return;
      if (safeLocalStorage('get', BANNER_DISMISSED_KEY) === 'true') return;

      const currentCount = parseInt(safeLocalStorage('get', VISIT_COUNT_KEY) || '0', 10);
      const newCount = currentCount + 1;
      safeLocalStorage('set', VISIT_COUNT_KEY, String(newCount));

      const tryShowBanner = (prompt: BeforeInstallPromptEvent) => {
        setDeferredPrompt(prompt);
        if (newCount >= 2) {
          setTimeout(() => setVisible(true), 2500);
        }
      };

      const existing = (window as any).__deferredInstallPrompt as BeforeInstallPromptEvent | undefined;
      if (existing) {
        tryShowBanner(existing);
      }

      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault();
        tryShowBanner(e as BeforeInstallPromptEvent);
      };

      const handlePwaInstallable = (e: Event) => {
        const detail = (e as CustomEvent).detail as BeforeInstallPromptEvent;
        if (detail) tryShowBanner(detail);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('pwa-installable', handlePwaInstallable);
      window.addEventListener('appinstalled', () => {
        safeLocalStorage('set', INSTALLED_KEY, 'true');
        setVisible(false);
      });

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('pwa-installable', handlePwaInstallable);
      };
    } catch {
      // Silently fail — banner is non-critical
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      setInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        safeLocalStorage('set', INSTALLED_KEY, 'true');
        (window as any).__deferredInstallPrompt = null;
        setVisible(false);
      }
    } catch {
      // Silently fail
    } finally {
      setInstalling(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    safeLocalStorage('set', BANNER_DISMISSED_KEY, 'true');
  };

  const handleRemindLater = () => {
    setVisible(false);
    safeLocalStorage('set', VISIT_COUNT_KEY, '0');
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 pointer-events-none"
      style={{ animation: 'slideUpBanner 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
    >
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-teal-400"></div>
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
              <i className="ri-smartphone-line text-2xl text-white"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-teal-600 uppercase tracking-widest mb-0.5">Install App</p>
                  <h3 className="text-base font-bold text-gray-900 leading-snug">Get faster access to Indoor-Restore</h3>
                </div>
                <button
                  onClick={handleDismiss}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex-shrink-0 mt-0.5"
                  aria-label="Dismiss"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                Add to your home screen for one-tap emergency access — works offline too.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {['⚡ Instant load', '📵 Works offline', '🔔 Quick access'].map((pill) => (
                  <span key={pill} className="text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full font-medium">
                    {pill}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleInstall}
                  disabled={installing}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {installing ? 'Installing…' : 'Install Now'}
                </button>
                <button
                  onClick={handleRemindLater}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  Remind me later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideUpBanner {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
